import React, { useCallback, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import GlobalWrapper from '../../../../../components/Wrapper/GlobalWrapper';
import GlobalTable from '../../../../../components/CustomTableWithPagination/GlobalTable';
import FromAndToDatePicker from '../../../../../components/mui/FromAndToDatePicker';
import GlobalSelectFieldWithoutFormikNew from '../../../../../components/mui/GlobalSelectFieldWithoutFormikNew';
import GlobalDialog from '../../../../../components/mui/Dialogue/GlobalDialog';
import ReplacementStatCard from '../../../../crd/myworkspace/tutorReplacements/components/ReplacementStatCard';
import { exportTrackerList } from '../../../../../redux/features/tutorReplacementTrackerSlice';
import useTrackerList from '../hooks/useTrackerList';
import { TRACKER_HEADERS } from '../utils/trackerTableHeader';
import { ASSIGNMENT_FILTERS, PROGRESS_FILTERS, TUTOR_STATUS_FILTERS, VIEWS } from '../utils/trackerConstants';
import AssignDialogContent from './AssignDialogContent';
import ProgressDialogContent from './ProgressDialogContent';
import TrackerHistory from './TrackerHistory';

const selectSx = (minWidth) => ({ minWidth: { xs: '100%', md: minWidth } });

// Shell shared by the TA head, TA associate and CRO pages: stat cards, filters and one table. Each page passes
// its view, cards and (for the head) the team; the actions in the table come from TRACKER_HEADERS[view].
// `cardRows` spreads the stat cards over that many rows on desktop. The list loads straight away, alongside the
// access check; `blocked` (a message) replaces the page once access says the user can't use it.
export default function TrackerPage({ view, title, cards, cardRows = 1, team = [], blocked }) {
  const { list, loading, reload, tableState, setTableState, exportParams, filters } = useTrackerList(view, {
    enabled: !blocked,
  });

  // { actionKey: 'assign' | 'progress' | 'history', row }
  const [modal, setModal] = useState({ actionKey: '', row: null });
  const closeModal = useCallback(() => setModal({ actionKey: '', row: null }), []);
  const handleButtonActionClick = useCallback((actionKey, row) => {
    if (actionKey && row) setModal({ actionKey, row });
  }, []);

  const summary = list.meta?.summary;
  const statCards = useMemo(() => cards.map((card) => ({ ...card, total: summary?.[card.key] ?? 0 })), [cards, summary]);

  const { row } = modal;
  const modalConfig = {
    assign: {
      title: 'Assign Replacement',
      render: () => (
        <AssignDialogContent
          row={row}
          team={team}
          onClose={closeModal}
          onSaved={() => {
            closeModal();
            reload();
          }}
        />
      ),
    },
    progress: {
      title: 'Update Progress',
      render: () => <ProgressDialogContent row={row} onClose={closeModal} onSaved={reload} />,
    },
    history: {
      title: `History — ${row?.studentName ?? ''}`,
      render: () => <TrackerHistory trainerAssignmentId={row?.trainerAssignmentId} />,
    },
  }[modal.actionKey];

  if (blocked) {
    return (
      <GlobalWrapper title={title}>
        <Typography color="text.secondary" py={4} textAlign="center">
          {blocked}
        </Typography>
      </GlobalWrapper>
    );
  }

  return (
    <GlobalWrapper title={title}>
      <GlobalDialog
        title={modalConfig?.title}
        open={Boolean(modalConfig)}
        setOpen={(value) => {
          if (!value) closeModal();
        }}
        closeButton={true}
        // GlobalDialog keeps its content mounted, so only render it while a row is open
        content={modalConfig ? modalConfig.render() : null}
        maxWidth={'sm'}
        fullWidth={true}
      />
      <Stack gap={2}>
        <Grid container spacing={1.5}>
          {statCards.map((card) => (
            <Grid item xs={6} sm={4} md={12 / Math.ceil(statCards.length / cardRows)} key={card.key}>
              <ReplacementStatCard title={card.title} total={loading ? '…' : card.total} icon={card.icon} color={card.color} />
            </Grid>
          ))}
        </Grid>

        <Stack direction={{ xs: 'column', md: 'row' }} gap={1} flexWrap="wrap">
          {view === VIEWS.HEAD && (
            <GlobalSelectFieldWithoutFormikNew
              data={ASSIGNMENT_FILTERS}
              keys={{ itemId: 'id', itemValue: 'name' }}
              label={'Assignment'}
              selectedDetails={filters.assignment}
              setDirectChange={filters.setAssignment}
              disabled={loading}
              sx={selectSx(160)}
            />
          )}
          <FromAndToDatePicker
            disabled={loading}
            fromDate={filters.fromDate}
            toDate={filters.toDate}
            setFromDate={filters.setFromDate}
            setToDate={filters.setToDate}
          />
          <GlobalSelectFieldWithoutFormikNew
            data={TUTOR_STATUS_FILTERS}
            keys={{ itemId: 'id', itemValue: 'name' }}
            label={'New Tutor Status'}
            selectedDetails={filters.tutorStatus}
            setDirectChange={filters.setTutorStatus}
            disabled={loading}
            sx={selectSx(200)}
          />
          <GlobalSelectFieldWithoutFormikNew
            data={PROGRESS_FILTERS}
            keys={{ itemId: 'id', itemValue: 'name' }}
            label={'Progress'}
            selectedDetails={filters.progress}
            setDirectChange={filters.setProgress}
            disabled={loading}
            sx={selectSx(160)}
          />
        </Stack>
      </Stack>
      <Box mt={1}>
        <GlobalTable
          rowUniqueId="trainerAssignmentId"
          headers={TRACKER_HEADERS[view]}
          rows={list.data}
          tableState={tableState}
          setTableState={setTableState}
          loading={loading}
          meta={list.meta}
          onButtonActionClick={handleButtonActionClick}
          exportApi={exportTrackerList}
          exportParams={exportParams}
        />
      </Box>
    </GlobalWrapper>
  );
}
